import { CallbackContext, JwtSignerJwk, verifyJwt } from "@openid4vc/oauth2";
import {
  IoWalletSdkConfig,
  ItWalletSpecsVersion,
  addSecondsToDate,
  dateToSeconds,
  encodeToBase64Url,
  parseWithErrorHandling,
} from "@pagopa/io-wallet-utils";

import { Jwk } from "../common/jwk/z-jwk";
import { decodeJwt } from "../common/jwt/decode-jwt";
import { Oauth2Error } from "../errors";
import {
  IT_WALLET_CLIENT_ATTESTATION_POP_ALLOWED_ALG_VALUES,
  ItWalletClientAttestationPopJwtHeader,
  ItWalletClientAttestationPopJwtPayload,
  zItWalletClientAttestationPopJwtAlg,
  zItWalletClientAttestationPopJwtHeader,
  zItWalletClientAttestationPopJwtPayload,
  zItWalletClientAttestationPopJwtTyp,
} from "./z-client-attestation-pop";

function assertSupportedClientAttestationPopAlg(
  alg: string,
): asserts alg is (typeof IT_WALLET_CLIENT_ATTESTATION_POP_ALLOWED_ALG_VALUES)[number] {
  if (!zItWalletClientAttestationPopJwtAlg.safeParse(alg).success) {
    throw new Oauth2Error(
      `Unsupported alg '${alg}' in client attestation PoP JWT: must be one of ${IT_WALLET_CLIENT_ATTESTATION_POP_ALLOWED_ALG_VALUES.join(", ")}`,
    );
  }
}

export interface VerifyClientAttestationPopJwtOptions {
  /**
   * The issuer identifier of the authorization server handling the client attestation
   */
  authorizationServer: string;

  /**
   * Callbacks used for verifying client attestation pop jwt.
   */
  callbacks: Pick<CallbackContext, "verifyJwt">;

  /**
   * The compact client attestation pop jwt.
   */
  clientAttestationPopJwt: string;

  /**
   * The public JWK to verify the client attestation pop jwt.
   */
  clientAttestationPublicJwk: Jwk;

  /**
   * Expected nonce in the payload. If not provided the nonce won't be validated.
   */
  expectedNonce?: string;

  /**
   * The current time to use when verifying the JWTs.
   * If not provided current time will be used.
   *
   * @default new Date()
   */
  now?: Date;
}

export type VerifiedClientAttestationPopJwt = Awaited<
  ReturnType<typeof verifyClientAttestationPopJwt>
>;

/**
 * Verifies a client attestation proof-of-possession JWT.
 *
 * @param options - Verification options.
 * @param options.authorizationServer - Expected audience, usually the Authorization Server issuer.
 * @param options.callbacks - Callback used to verify the JWT signature.
 * @param options.clientAttestationPopJwt - Compact client attestation PoP JWT.
 * @param options.clientAttestationPublicJwk - Public JWK from the wallet/client attestation confirmation claim.
 * @param options.expectedNonce - Optional nonce expected in the JWT payload.
 * @param options.now - Date used for temporal validation.
 * @returns Decoded JWT header, payload, and resolved signer.
 * @throws {Oauth2Error} If decoding, algorithm validation, signature verification, or claim validation fails.
 */
export async function verifyClientAttestationPopJwt(
  options: VerifyClientAttestationPopJwtOptions,
) {
  try {
    const { header, payload } = decodeJwt({
      errorMessagePrefix: "Error decoding client attestation PoP JWT:",
      headerSchema: zItWalletClientAttestationPopJwtHeader,
      jwt: options.clientAttestationPopJwt,
      payloadSchema: zItWalletClientAttestationPopJwtPayload,
    });

    assertSupportedClientAttestationPopAlg(header.alg);

    const { signer } = await verifyJwt({
      compact: options.clientAttestationPopJwt,
      errorMessage: "client attestation pop jwt verification failed",
      expectedAudience: options.authorizationServer,
      expectedNonce: options.expectedNonce,
      header,
      now: options.now,
      payload,
      signer: {
        alg: header.alg,
        method: "jwk",
        publicJwk: options.clientAttestationPublicJwk,
      },
      verifyJwtCallback: options.callbacks.verifyJwt,
    });

    return {
      header,
      payload,
      signer,
    };
  } catch (error) {
    if (error instanceof Oauth2Error) throw error;
    throw new Oauth2Error(
      `Error creating client attestation pop jwt : ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

interface BaseCreateClientAttestationPopJwtOptions<
  V extends ItWalletSpecsVersion,
> {
  /**
   * The audience authorization server identifier
   */
  authorizationServer: string;

  /**
   * Callback used for dpop
   * generateRandom is mandatory if jti is not provided
   */
  callbacks: Partial<Pick<CallbackContext, "generateRandom">> &
    Pick<CallbackContext, "signJwt">;

  /**
   * The client attestation to create the Pop for
   */
  clientAttestation: string;

  /**
   * The IT-Wallet SDK configuration, used to select the correct version-specific
   * payload shape for the client attestation PoP JWT.
   */
  config: IoWalletSdkConfig<V>;

  /**
   * Creation time of the JWT. If not provided the current date will be used
   */
  issuedAt?: Date;

  /**
   * Optional jti to set in the payload. If not provided a random one will be generated
   */
  jti?: string;

  /**
   * The signer of jwt. Only jwk signer allowed.
   *
   * If not provided, the signer will be derived based on the
   * `cnf.jwk` and `alg` in the client attestation.
   */
  signer?: JwtSignerJwk;
}

type NoClientAttestationPopJwtExtraOptions = Record<never, never>;

interface CreateClientAttestationPopJwtVersionOptions {
  [ItWalletSpecsVersion.V1_0]: {
    /**
     * Expiration time of the JWT.
     * If not provided, 1 minute will be added to `issuedAt`.
     */
    expiresAt?: Date;
  };
  [ItWalletSpecsVersion.V1_3]: NoClientAttestationPopJwtExtraOptions;
  [ItWalletSpecsVersion.V1_4]: NoClientAttestationPopJwtExtraOptions;
}

type CreateClientAttestationPopJwtExtraOptions<V extends ItWalletSpecsVersion> =
  [V] extends [keyof CreateClientAttestationPopJwtVersionOptions]
    ? CreateClientAttestationPopJwtVersionOptions[V]
    : NoClientAttestationPopJwtExtraOptions;

export type CreateClientAttestationPopJwtOptionsForVersion<
  V extends ItWalletSpecsVersion,
> = BaseCreateClientAttestationPopJwtOptions<V> &
  CreateClientAttestationPopJwtExtraOptions<V>;

export type CreateClientAttestationPopJwtOptionsV1_0 =
  CreateClientAttestationPopJwtOptionsForVersion<ItWalletSpecsVersion.V1_0>;

export type CreateClientAttestationPopJwtOptionsV1_3 =
  CreateClientAttestationPopJwtOptionsForVersion<ItWalletSpecsVersion.V1_3>;

export type CreateClientAttestationPopJwtOptionsV1_4 =
  CreateClientAttestationPopJwtOptionsForVersion<ItWalletSpecsVersion.V1_4>;

export type CreateClientAttestationPopJwtOptions =
  CreateClientAttestationPopJwtOptionsForVersion<ItWalletSpecsVersion>;

/**
 * Creates a client attestation proof-of-possession JWT.
 *
 * The signer is derived from the wallet/client attestation `cnf.jwk` when it is
 * not supplied explicitly.
 *
 * @param options - Client attestation PoP creation options.
 * @returns Compact signed client attestation PoP JWT.
 * @throws {Oauth2Error} If required attestation claims are missing, `jti` cannot be generated,
 * or signing/validation fails.
 */
export async function createClientAttestationPopJwt<
  V extends ItWalletSpecsVersion = ItWalletSpecsVersion,
>(options: CreateClientAttestationPopJwtOptionsForVersion<V>): Promise<string> {
  try {
    const clientAttestation = decodeJwt({
      errorMessagePrefix: "Error decoding client attestation JWT:",
      jwt: options.clientAttestation,
    });

    const jwk = clientAttestation.payload.cnf?.jwk;
    if (!jwk) {
      throw new Oauth2Error(
        "Client attestation does not contain 'cnf.jwk', cannot create client attestation pop jwt",
      );
    }

    const sub = clientAttestation.payload.sub;
    if (!sub || typeof sub !== "string") {
      throw new Oauth2Error(
        "Client attestation does not contain 'sub', cannot create client attestation pop jwt",
      );
    }

    const signer = options.signer ?? {
      alg: clientAttestation.header.alg,
      method: "jwk",
      publicJwk: jwk,
    };

    assertSupportedClientAttestationPopAlg(signer.alg);

    const header = {
      alg: signer.alg,
      typ: zItWalletClientAttestationPopJwtTyp.value,
    } satisfies ItWalletClientAttestationPopJwtHeader;

    const issuedAt = options.issuedAt ?? new Date();
    const jti =
      options.jti ??
      (options.callbacks.generateRandom
        ? encodeToBase64Url(await options.callbacks.generateRandom(32))
        : undefined);

    if (!jti) {
      throw new Oauth2Error(
        "Error: neither a default jti nor a generateRandom callback have been provided",
      );
    }

    const payload = parseWithErrorHandling(
      zItWalletClientAttestationPopJwtPayload,
      {
        aud: options.authorizationServer,
        iat: dateToSeconds(issuedAt),
        iss: sub,
        jti,
        ...(options.config.itWalletSpecsVersion === ItWalletSpecsVersion.V1_0
          ? {
              exp: dateToSeconds(
                "expiresAt" in options && options.expiresAt
                  ? options.expiresAt
                  : addSecondsToDate(issuedAt, 1 * 60),
              ),
            }
          : {}),
      } satisfies ItWalletClientAttestationPopJwtPayload,
    );

    const { jwt } = await options.callbacks.signJwt(signer, {
      header,
      payload,
    });

    return jwt;
  } catch (error) {
    if (error instanceof Oauth2Error) throw error;
    throw new Oauth2Error(
      `Error creating client attestation pop jwt : ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}
